import { Dispatch } from "@reduxjs/toolkit";
import type { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import { ApiError } from "@/app/common-types/api-error.types";
import { getBrandDomain } from "@/app/common-utils/get-brand-domain.utils";
import {
  Brand,
  DatesEdit,
  DestinationEdit,
  QuestionFive,
  QuestionThree,
  QuoteDecline,
  QuoteErrorCodesEnum,
} from "@/config/app-constant";
import { MY_ACCOUNT_URL } from "@/constants";
import fetchQuoteError from "@/hooks/magnolia/fetch-quote-errors";
import fetchSessionExpireError from "@/hooks/magnolia/fetch-session-expire";
import { getWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { setApiError } from "@/store/slices/errors/api-error.action";
import {
  setQuote,
  setQuestionData,
  setQuoteErrorCode,
} from "@/store/slices/quote/quote.slice";
import { QuotePayload } from "@/types/quote/quote-data";
import { QuoteData } from "@/types/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

type ErrorHandler = (
  error: ApiError,
  dispatch: ReturnType<typeof useDispatch>,
  router?: ReturnType<typeof useRouter>,
  handleOnClick?: () => void,
  errorCode?: number | null
) => Promise<void>;

const errorHandlers: Record<number, ErrorHandler> = {
  [QuoteErrorCodesEnum.INVALID_START_DATE]: async (
    error: ApiError,
    dispatch: Dispatch,
    router,
    handleOnClick,
    errorCode
  ) => {
    fetchQuoteError(
      QuoteErrorCodesEnum.INVALID_START_DATE as unknown as string
    ).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.multiButton = true;
      if (data?.title) {
        errorObject.modalTitle = data?.title;
      }
      if (data?.modalDescription) {
        errorObject.modalDescription = data?.modalDescription;
      }
      if (data?.ctaPrimaryBtnLabel) {
        errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
      }
      if (data?.ctaSecondaryBtnLabel) {
        errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
      }
      // Translate path and store it in Redux state (serializable)
      const translatedPath = await getWizardTranslation(
        "dates_path",
        undefined,
        QuestionFive
      );
      errorObject.redirectPath = `/${translatedPath}/?${DatesEdit}=true`;
      dispatch(setApiError(errorObject));
      const errorResponse = error.response?.data;
      if (errorResponse) {
        const quoteResponse = errorResponse as { data: QuoteData };
        sessionStorage.setItem("initialLoad", "true");
        dispatch(
          setQuestionData({
            ...(quoteResponse.data as QuotePayload),
            quoteCompleted: { completed: false },
          })
        );
        dispatch(
          setQuote({
            ...quoteResponse.data,
            quoteCompleted: { completed: false },
          })
        );
        if (errorCode) dispatch(setQuoteErrorCode(errorCode));
      }
    });
  },
  [QuoteErrorCodesEnum.FCDO_RESTRICTED]: async (
    error: ApiError,
    dispatch: Dispatch,
    router,
    handleOnClick,
    errorCode
  ) => {
    fetchQuoteError(
      QuoteErrorCodesEnum.FCDO_RESTRICTED as unknown as string
    ).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.multiButton = true;
      if (data?.title) {
        errorObject.modalTitle = data?.title;
      }
      if (data?.modalDescription) {
        errorObject.modalDescription = data?.modalDescription;
      }
      if (data?.ctaPrimaryBtnLabel) {
        errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
      }
      if (data?.ctaSecondaryBtnLabel) {
        errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
      }
      // Translate path and store it in Redux state (serializable)
      const translatedPath = await getWizardTranslation(
        "destination_path",
        undefined,
        QuestionThree
      );
      errorObject.redirectPath = `/${translatedPath}/?${DestinationEdit}=true`;
      dispatch(setApiError(errorObject));
      const errorResponse = error.response?.data;
      if (errorResponse) {
        const quoteResponse = errorResponse as { data: QuoteData };
        sessionStorage.setItem("initialLoad", "true");
        dispatch(
          setQuestionData({
            ...(quoteResponse.data as QuotePayload),
            quoteCompleted: { completed: false },
          })
        );
        dispatch(
          setQuote({
            ...quoteResponse.data,
            quoteCompleted: { completed: false },
          })
        );
        if (errorCode) dispatch(setQuoteErrorCode(errorCode));
      }
    });
  },
  [QuoteErrorCodesEnum.TRIP_LIMITATION_AGE]: async (
    error: ApiError,
    dispatch: Dispatch,
    router
  ) => {
    fetchQuoteError(
      QuoteErrorCodesEnum.TRIP_LIMITATION_AGE as unknown as string
    ).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.multiButton = true;
      if (data?.title) {
        errorObject.modalTitle = data?.title;
      }
      if (data?.modalDescription) {
        errorObject.modalDescription = data?.modalDescription;
      }
      if (data?.ctaPrimaryBtnLabel) {
        errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
      }
      if (data?.ctaSecondaryBtnLabel) {
        errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
      }
      // Translate path and store it in Redux state (serializable)
      const translatedPath = await getWizardTranslation(
        "destination_path",
        undefined,
        QuestionThree
      );
      errorObject.redirectPath = `/${translatedPath}/?${DestinationEdit}=true`;
      dispatch(setApiError(errorObject));
      const errorResponse = error.response?.data;
      if (errorResponse) {
        const quoteResponse = errorResponse as { data: QuoteData };
        sessionStorage.setItem("initialLoad", "true");
        dispatch(
          setQuestionData({
            ...(quoteResponse.data as QuotePayload),
            quoteCompleted: { completed: false },
          })
        );
        dispatch(
          setQuote({
            ...quoteResponse.data,
            quoteCompleted: { completed: false },
          })
        );
      }
    });
  },
  1078: async (error, dispatch, _, handleOnClick) => {
    const errorObject = await apiErrorMappings(error);
    errorObject.validationEnable = true;
    errorObject.multiButton = false;
    errorObject.ctaPrimaryBtnLabel = "Login";
    errorObject.onPrimaryCtaClick = handleOnClick;
    dispatch(setApiError(errorObject));
  },
  [QuoteErrorCodesEnum.EXPIRED]: async (error, dispatch) => {
    fetchQuoteError(QuoteErrorCodesEnum.EXPIRED as unknown as string).then(
      async (data) => {
        const errorObject = await apiErrorMappings(error);
        errorObject.multiButton = true;
        if (data?.title) {
          errorObject.modalTitle = data?.title;
        }
        if (data?.modalDescription) {
          errorObject.modalDescription = data?.modalDescription;
        }
        if (data?.ctaPrimaryBtnLabel) {
          errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
        }
        if (data?.ctaSecondaryBtnLabel) {
          errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
        }
        dispatch(setApiError(errorObject));
      }
    );
  },
  [QuoteErrorCodesEnum.MEDICAL_DECLINED]: async (error, dispatch, router) => {
    fetchQuoteError(
      QuoteErrorCodesEnum.MEDICAL_DECLINED as unknown as string
    ).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.multiButton = true;
      if (data?.title) {
        errorObject.modalTitle = data?.title;
      }
      if (data?.modalDescription) {
        errorObject.modalDescription = data?.modalDescription;
      }
      if (data?.ctaPrimaryBtnLabel) {
        errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
      }
      if (data?.ctaSecondaryBtnLabel) {
        errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
      }
      // Store redirect path for medical declined (used on close icon click)
      const translatedPath = await getWizardTranslation(
        "unable_to_quote_path",
        undefined,
        QuoteDecline
      );
      errorObject.redirectPath = `/${translatedPath}`;
      dispatch(setApiError(errorObject));
    });
  },
  [QuoteErrorCodesEnum.ALREADY_PURCHASED]: async (error, dispatch, router) => {
    fetchQuoteError(
      QuoteErrorCodesEnum.ALREADY_PURCHASED as unknown as string
    ).then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.multiButton = true;
      if (data?.title) {
        errorObject.modalTitle = data?.title;
      }
      if (data?.modalDescription) {
        errorObject.modalDescription = data?.modalDescription;
      }
      if (data?.ctaPrimaryBtnLabel) {
        errorObject.ctaPrimaryBtnLabel = data.ctaPrimaryBtnLabel;
      }
      if (data?.ctaSecondaryBtnLabel) {
        errorObject.ctaSecondaryBtnLabel = data.ctaSecondaryBtnLabel;
      }
      const brand = getBrandDomain();
      if (brand === Brand.STAYSURE || brand === Brand.AVANTI) {
        errorObject.redirectPath = MY_ACCOUNT_URL!;
      }
      dispatch(setApiError(errorObject));
    });
  },
  4001: async (error, dispatch, router) => {
    fetchSessionExpireError().then(async (data) => {
      const errorObject = await apiErrorMappings(error);
      errorObject.errorStatus = true;
      errorObject.validationEnable = false;
      errorObject.multiButton = false;
      errorObject.ctaPrimaryBtnLabel =
        data?.ctaButtonText ?? "Back to My Staysure";
      errorObject.modalTitle =
        data?.title ?? "Your session has <br/> timed out";
      errorObject.modalDescription = "";
      errorObject.isCloseIconEnabled = false;
      errorObject.redirectPath = process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!;

      dispatch(setApiError(errorObject));
    });
  },
};

export default errorHandlers;
